<template>
  <!-- 选中建筑的信息弹窗：贴着名称标牌右侧长出来，跟着镜头飘。
       注意 @pointerdown/@click 的 .stop —— 弹窗是地图容器的子节点，
       不拦住的话点弹窗会冒泡成"点地图"（表现为一点就把选中取消）。 -->
  <Transition name="park-pop">
    <div v-if="shown" ref="wrapRef" class="park-panel-wrap" @pointerdown.stop @click.stop>
      <div class="park-panel">
        <div class="park-panel__head">
          <span class="park-panel__title">{{ overviewTitle }}</span>
          <button type="button" class="park-panel__close" @click.stop="$emit('close')">×</button>
        </div>

        <p class="park-panel__intro">{{ introText }}</p>

        <div class="park-panel__stats">
          <div v-for="card in statCards" :key="card.label" class="park-panel__stat">
            <span class="park-panel__stat-label">{{ card.label }}</span>
            <b class="park-panel__stat-value">{{ card.value }}<small v-if="card.unit">{{ card.unit }}</small></b>
          </div>
        </div>

        <div class="park-panel__charts">
          <div class="park-panel__chart-block">
            <p class="park-panel__chart-title">商户入驻率</p>
            <div class="park-panel__chart-body">
              <svg class="park-panel__ring" viewBox="0 0 72 72">
                <circle cx="36" cy="36" r="26" fill="none" stroke="#efeff6" stroke-width="10" />
                <circle v-if="rentedTotal > 0" cx="36" cy="36" r="26" fill="none" stroke="#7b5bf2" stroke-width="10"
                  stroke-linecap="round" :stroke-dasharray="rentedArc + ' ' + (CIRC - rentedArc)"
                  transform="rotate(-90 36 36)" />
                <text x="36" y="40" text-anchor="middle" font-size="12" font-weight="700" fill="#262b43">
                  {{ rentedRateText }}
                </text>
              </svg>
              <ul class="park-panel__legend">
                <li v-for="seg in rentedLegend" :key="seg.name">
                  <i :style="{ background: seg.color }"></i>{{ seg.name }}<b>{{ seg.value }}</b>
                </li>
              </ul>
            </div>
          </div>
          <div class="park-panel__chart-block">
            <p class="park-panel__chart-title">商户品类占比</p>
            <div v-if="scopeSegments.length" class="park-panel__chart-body">
              <svg class="park-panel__ring" viewBox="0 0 72 72">
                <circle cx="36" cy="36" r="26" fill="none" stroke="#efeff6" stroke-width="10" />
                <circle v-for="seg in scopeSegments" :key="seg.name" cx="36" cy="36" r="26" fill="none"
                  :stroke="seg.color" stroke-width="10" stroke-linecap="round"
                  :stroke-dasharray="seg.arc + ' ' + (CIRC - seg.arc)" :stroke-dashoffset="-seg.offset"
                  transform="rotate(-90 36 36)" />
                <text x="36" y="40" text-anchor="middle" font-size="12" font-weight="700" fill="#262b43">
                  {{ scopeTotalText }}
                </text>
              </svg>
              <ul class="park-panel__legend">
                <li v-for="seg in scopeSegments" :key="seg.name">
                  <i :style="{ background: seg.color }"></i>{{ seg.name }}<b>{{ seg.value }}</b>
                </li>
              </ul>
            </div>
            <p v-else class="park-panel__chart-empty">暂无品类数据</p>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { AREA_ID_BY_BUILDING, fetchAreaOverview } from '@/services/areaOverview'

/**
 * 建筑信息弹窗：点中 23 栋编号楼（楼体或上方名称牌）后，贴着那块名称牌摆一张实体卡，
 * 内容 = 「区域概览」接口（src/services/areaOverview.js）：名称/介绍 + 四指标卡 +
 * 入驻率环 + 品类占比环。版式沿用 src/components/test.vue 的 park-panel 参考稿。
 *
 * ⚠️ 定位刻意不走 Vue：标牌屏幕位置每帧都在变（相机飞行 + 阻尼），把它塞进响应式
 * 数据就等于每帧新建一个 selection 对象，整块驾驶舱跟着重渲染 —— sceneView 里
 * 「点一下建筑很卡」那半边成因就是这么来的。所以这里只自带一个 rAF，
 * 从 scene.getSelectionScreen() 读实时投影，直接写 style.left/top。
 */

const props = defineProps({
  /** sceneView 的 onSelect 投递：{ code, name, screen, labelRight, visible, panel } */
  selected: { type: Object, default: null },
  /** () => 选中标牌的实时屏幕位置（见 sceneView 的 selScreen） */
  getScreen: { type: Function, required: true },
})

defineEmits(['close'])

const wrapRef = ref(null)

// ---- 出现时机 ----
// 点选后相机要飞过去（落点见 sceneView 的 FLY_ANCHOR），弹窗等飞行结束再弹，
// 否则会先出现在旧机位上、再被镜头甩到一边。
const CAMERA_FLY_MS = 360
const ready = ref(false)
let readyTimer = 0

// ---- 数据 ----
const overview = ref(null)
// idle=未请求 loading=请求中 ready=已拿到数据 error=请求失败 none=该楼未配区域ID
const overviewState = ref('idle')
// 请求序号：快速切换楼时丢弃过期响应，防竞态
let overviewReq = 0
// 同一区域在整个会话里只打一次接口（反复点同一栋楼不该反复请求）
const overviewCache = new Map()

async function loadOverview(name) {
  const areaId = AREA_ID_BY_BUILDING[name]
  overviewReq += 1
  const seq = overviewReq
  if (!areaId) {
    overview.value = null
    overviewState.value = 'none'
    return
  }
  if (overviewCache.has(areaId)) {
    overview.value = overviewCache.get(areaId)
    overviewState.value = 'ready'
    return
  }
  overviewState.value = 'loading'
  try {
    const data = await fetchAreaOverview(areaId)
    overviewCache.set(areaId, data || null) // 区域不存在时 data 为 null，由展示层兜底
    if (seq !== overviewReq) return // 已切到别的楼，丢弃过期响应
    overview.value = data
    overviewState.value = 'ready'
  } catch (e) {
    console.warn('[BuildingInfoPopup] 区域概览拉取失败:', e)
    if (seq !== overviewReq) return
    overview.value = null
    overviewState.value = 'error'
  }
}

// 环形图几何：viewBox 72、半径 26，周长 2πr ≈ 163.4
const RING_R = 26
const CIRC = Math.round(2 * Math.PI * RING_R * 10) / 10

// 品类环配色（数据段依次取色，超出后循环）
const SCOPE_COLORS = ['#7b5bf2', '#f17eb8', '#f6a94a', '#4fc3d9', '#b0a4f5', '#7bd8a8']

/** 大数缩写：15438.50 -> { value: '1.5', unit: 'w' }（经营面积按文档建议除 1 万显示） */
function fmtWan(v) {
  if (v === null || v === undefined || v === '') return null
  const n = Number(v)
  if (!Number.isFinite(n)) return null
  return { value: (Math.round((n / 10000) * 10) / 10).toFixed(1), unit: 'w' }
}

/** 整数字段兜底：null/undefined/'' -> '—'（0 是合法值，照常展示） */
function fmtCount(v) {
  if (v === null || v === undefined || v === '') return '—'
  const n = Number(v)
  return Number.isFinite(n) ? String(Math.round(n)) : '—'
}

const data = computed(() => (overviewState.value === 'ready' ? overview.value : null))

// 面板标题：接口的区域名优先，退回标牌名
const overviewTitle = computed(() => {
  if (data.value && data.value.areaName) return data.value.areaName
  return props.selected ? props.selected.name : ''
})

// 简介段：接口 areaIntro 优先，各兜底态给占位文案
const introText = computed(() => {
  if (overviewState.value === 'loading') return '区域数据加载中…'
  const d = data.value
  if (d && d.areaIntro) return d.areaIntro
  if (d && d.areaName) return `${d.areaName}暂无介绍。`
  const s = props.selected
  return s ? `${s.name}（${s.code} 号楼）暂无区域介绍。` : '—'
})

// 四指标卡：接口字段可空（文档口径），缺数据统一显示 —
const statCards = computed(() => {
  const d = data.value
  const wan = d ? fmtWan(d.operatingArea) : null
  return [
    { label: '经营面积(m²)', value: wan ? wan.value : '—', unit: wan ? wan.unit : '' },
    { label: '商户数(家)', value: d ? fmtCount(d.totalMerchants) : '—', unit: '' },
    { label: '从业人员(人)', value: d ? fmtCount(d.practitioner) : '—', unit: '' },
    // 昨日客流无数据时接口回 0，是正常值，直接展示
    { label: '昨日客流量(人)', value: d ? fmtCount(d.yesterdayNumber) : '—', unit: '' },
  ]
})

// 入驻率环：入驻/(入驻+空置)；rentedCount/vacantCount 任一为空按无数据处理
const rentedTotal = computed(() => {
  const d = data.value
  if (!d) return 0
  const rented = d.rentedCount
  const vacant = d.vacantCount
  if (rented === null || rented === undefined || vacant === null || vacant === undefined) return 0
  const a = Number(rented)
  const b = Number(vacant)
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 0
  return a + b
})

const rentedArc = computed(() => {
  if (rentedTotal.value <= 0) return 0
  const rented = Number(overview.value.rentedCount) || 0
  return (rented / rentedTotal.value) * CIRC
})

const rentedRateText = computed(() => {
  if (rentedTotal.value <= 0) return '—'
  const rented = Number(overview.value.rentedCount) || 0
  return Math.round((rented / rentedTotal.value) * 100) + '%'
})

const rentedLegend = computed(() => {
  const d = data.value
  if (!d || rentedTotal.value <= 0) {
    return [
      { name: '入驻', value: '—', color: '#7b5bf2' },
      { name: '空置', value: '—', color: '#f2b8cf' },
    ]
  }
  return [
    { name: '入驻', value: fmtCount(d.rentedCount), color: '#7b5bf2' },
    { name: '空置', value: fmtCount(d.vacantCount), color: '#f2b8cf' },
  ]
})

// 品类占比环：businessScopes 逐段绘制（dasharray + 累计 offset），环心显总商户数
const scopeSegments = computed(() => {
  const d = data.value
  const scopes = d && Array.isArray(d.businessScopes) ? d.businessScopes : []
  const total = scopes.reduce((sum, it) => sum + (Number(it.shopCount) || 0), 0)
  if (!total) return []
  let acc = 0
  return scopes.slice(0, SCOPE_COLORS.length).map((it, i) => {
    const count = Number(it.shopCount) || 0
    const arc = (count / total) * CIRC
    const seg = {
      name: it.name || '其他',
      value: count,
      color: SCOPE_COLORS[i % SCOPE_COLORS.length],
      arc,
      offset: acc,
    }
    acc += arc
    return seg
  })
})

const scopeTotalText = computed(() => {
  const d = data.value
  const scopes = d && Array.isArray(d.businessScopes) ? d.businessScopes : []
  const total = scopes.reduce((sum, it) => sum + (Number(it.shopCount) || 0), 0)
  return total ? String(total) : '—'
})

// ---- 定位：面板贴着名称标牌 ----
// 以标牌（气泡底部）为锚点摆在右侧，像从牌子上长出来。放不下时贴边，不沉底。
// wrap 的 3D 倾斜支点（10% 50%，贴标牌一侧）就是铰接点。
const PW = 290
const PH = 440
// 面板左缘离标牌右缘的距离（标牌宽度随楼名长度变化，labelRight 由场景侧每帧投影给出），
// 不压牌子尾部；拿不到右缘时退回锚点 + 固定间距。
// 卡片还有 3D 倾斜和 5px 板厚往左露头，所以实际视觉间距比这个值小几像素。
const PANEL_GAP_X = 130

function applyPos(el, x, y, labelRight) {
  const host = el.offsetParent
  const w = host ? host.clientWidth : 1600
  const h = host ? host.clientHeight : 900
  const anchorX = Number.isFinite(labelRight) ? labelRight : x + 90
  const left = Math.min(Math.max(12, anchorX + PANEL_GAP_X), Math.max(12, w - PW - 12))
  // 顶边略高于标牌锚点，底边放不下时上收
  const top = Math.min(Math.max(12, y - 34), Math.max(12, h - PH - 12))
  el.style.left = `${Math.round(left)}px`
  el.style.top = `${Math.round(top)}px`
}

function positionNow() {
  const el = wrapRef.value
  if (!el) return
  const s = props.getScreen()
  const sel = props.selected
  // 楼号对不上（刚换楼、场景侧还没刷新）时退回本次投递的坐标，先摆个大致位置
  if (!s || !sel || s.code !== String(sel.code)) {
    if (sel && sel.screen) applyPos(el, sel.screen.x, sel.screen.y, sel.labelRight)
    return
  }
  applyPos(el, s.x, s.y, s.labelRight)
}

let raf = 0
function frame() {
  raf = requestAnimationFrame(frame)
  positionNow()
}

const shown = computed(() => {
  const s = props.selected
  return !!(s && s.visible && s.panel && ready.value)
})

watch(
  () => (props.selected ? String(props.selected.code) : ''),
  (code, prev) => {
    if (!code) {
      clearTimeout(readyTimer)
      ready.value = false
      return
    }
    if (code === prev) return // 同一栋楼重复点击：不重延时，也不重拉数据
    ready.value = false
    clearTimeout(readyTimer)
    readyTimer = setTimeout(() => {
      ready.value = true
    }, CAMERA_FLY_MS)
    loadOverview(props.selected.name)
  },
  { immediate: true },
)

watch(shown, (v) => {
  if (v) {
    // 挂载后立刻摆好位置再交给 rAF，避免出现一帧"没定位就杵在左上角"的闪动
    nextTick(positionNow)
    if (!raf) raf = requestAnimationFrame(frame)
  } else if (raf) {
    cancelAnimationFrame(raf)
    raf = 0
  }
})

onBeforeUnmount(() => {
  clearTimeout(readyTimer)
  if (raf) cancelAnimationFrame(raf)
  raf = 0
})
</script>

<style scoped>
/* ---- 选中建筑的信息面板：参考稿白卡 + 紫灰板厚，数据来自区域概览接口 ---- */
.park-panel-wrap {
  position: absolute;
  z-index: 200; /* 与校准面板同层：盖在驾驶舱的 autofit 容器（z-index:100）之上 */
  width: 290px;
  pointer-events: none;
  /* 立体感靠"透视 + 板厚"：支点放在左缘中点（贴名称标牌一侧），
     牌子基本是"立着"的，只是略微转向观察者 */
  transform: perspective(1250px) rotateY(-11deg) rotateX(3deg);
  transform-origin: 10% 50%;
}

.park-panel {
  position: relative;
  pointer-events: auto;
  width: 290px;
  box-sizing: border-box;
  border-radius: 14px;
  background: #ffffff;
  border: 1px solid #eef0f6;
  box-shadow: 0 18px 44px rgba(23, 26, 54, 0.18), 0 4px 12px rgba(23, 26, 54, 0.08);
  color: #262b43;
  font-family: 'Microsoft YaHei', sans-serif;
}

/* 板厚：右下偏移的一层深色面，让卡片从"贴图"变成"实体板"。
   注意不能给 .park-panel 加 overflow:hidden —— 会把露在卡外的板厚裁掉 */
.park-panel::before {
  content: '';
  position: absolute;
  left: 5px;
  top: 6px;
  right: -6px;
  bottom: -6px;
  border-radius: 14px;
  background: linear-gradient(160deg, #b9bdf0 0%, #7d81c9 100%);
  z-index: -1;
  box-shadow: 0 22px 44px rgba(23, 26, 54, 0.26);
}

.park-panel__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 14px 14px 0;
  font-size: 15px;
  font-weight: 700;
}

.park-panel__title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.park-panel__close {
  flex: none;
  width: 22px;
  height: 22px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: #efeff6;
  color: #7a7f99;
  cursor: pointer;
  line-height: 22px;
  font-family: inherit;
}

.park-panel__close:hover {
  background: #e3e4ef;
  color: #51566f;
}

.park-panel__intro {
  margin: 8px 14px 0;
  font-size: 11px;
  line-height: 1.6;
  color: #7a7f99;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow: hidden;
}

.park-panel__stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin: 12px 14px 0;
}

.park-panel__stat {
  padding: 9px 10px;
  border-radius: 10px;
  background: #f5f5fa;
}

.park-panel__stat-label {
  display: block;
  font-size: 10px;
  color: #7a7f99;
  white-space: nowrap;
}

.park-panel__stat-value {
  display: block;
  margin-top: 3px;
  font-size: 16px;
  color: #262b43;
  font-variant-numeric: tabular-nums;
}

.park-panel__stat-value small {
  margin-left: 1px;
  font-size: 10px;
  font-weight: 400;
  color: #7a7f99;
}

/* 商户入驻率 / 商户品类占比：两个环图左右并排 */
.park-panel__charts {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin: 12px 14px 14px;
}

.park-panel__chart-block {
  padding: 10px 0 0;
  border-top: 1px solid #f0f1f7;
}

.park-panel__chart-title {
  margin: 0 0 8px;
  font-size: 12px;
  font-weight: 700;
  color: #262b43;
}

/* 单块内部纵排：环在上、图例在下 */
.park-panel__chart-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.park-panel__ring {
  flex: none;
  width: 72px;
  height: 72px;
}

.park-panel__chart-empty {
  margin: 22px 0 24px;
  font-size: 11px;
  color: #a3a8c3;
  text-align: center;
}

.park-panel__legend {
  list-style: none;
  margin: 0;
  padding: 0;
  width: 100%;
}

.park-panel__legend li {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 5px 0;
  font-size: 11px;
  color: #7a7f99;
}

.park-panel__legend i {
  flex: none;
  width: 7px;
  height: 7px;
  border-radius: 50%;
}

.park-panel__legend b {
  margin-left: auto;
  font-weight: 400;
  color: #262b43;
  font-variant-numeric: tabular-nums;
}

/* 详情面板的出现/消失动画：带着 3D 倾斜一起从标牌一侧缩放淡入淡出 */
.park-pop-enter-active {
  transition: opacity 0.26s ease, transform 0.3s cubic-bezier(0.22, 0.9, 0.28, 1.25);
}

.park-pop-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease-in;
}

.park-pop-enter-from {
  opacity: 0;
  transform: perspective(1250px) rotateY(-11deg) rotateX(3deg) scale(0.86) translateX(-16px);
}

.park-pop-leave-to {
  opacity: 0;
  transform: perspective(1250px) rotateY(-11deg) rotateX(3deg) scale(0.92) translateX(-10px);
}
</style>
